import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { defaultTo } from 'ramda';

import { useAppContext } from '../../../../../../../../AppModulesWrapper';

import { selectActiveServiceId, selectServiceIds } from '../../../../../../../reducers/v2/promotion/selectors';
import { selectAnalyticsProduct, selectAnalyticsDiffPricingIds } from '../../../../../../../selectors/analytics';
import { selectCurrentBet } from '../../../../../../../selectors/auction';
import { selectBetPosition } from '../../../../../../../selectors/betPosition';
import { selectExperiments } from '../../../../../../../selectors/experiments';
import {
  servicesCardsTextsExperimentName,
  TServicesCardsTextsExperimentGroupsNames,
} from '../../../../../../../constants/experiments';

import { SERVICES_CARDS_EXPERIMENT_EVENT_TYPE } from '../constants';
import {
  IUseServicesAnalytics,
  ITrackServicesShowExtra,
  ITrackDiffPricingShowExtra,
  ITrackChangeServiceExtra,
  ITrackSliderScrollExtra,
  TSliderScrollDirection,
} from '../types';

export const useServicesAnalytics = (): IUseServicesAnalytics => {
  const { analytics } = useAppContext();

  const activeServiceId = useSelector(selectActiveServiceId)!;
  const serviceIds = useSelector(selectServiceIds);
  const analyticsProduct = useSelector(selectAnalyticsProduct);
  const diffPricingIds = useSelector(selectAnalyticsDiffPricingIds);
  const bet = useSelector(selectCurrentBet);
  const betPosition = useSelector(selectBetPosition);
  const experiments = useSelector(selectExperiments);

  const servicesExperimentType = defaultTo(
    SERVICES_CARDS_EXPERIMENT_EVENT_TYPE.a_default,
    SERVICES_CARDS_EXPERIMENT_EVENT_TYPE[
      experiments[servicesCardsTextsExperimentName]! as TServicesCardsTextsExperimentGroupsNames
    ],
  );

  const trackServicesShow = useCallback(() => {
    analytics.send<ITrackServicesShowExtra>({
      action: 'publish_terms_block_show',
      label: serviceIds.join('/'),
      products: [analyticsProduct],
      extra: {
        type: servicesExperimentType,
        vas: activeServiceId,
        bid: bet,
        position: betPosition && betPosition.offerPosition,
        page_number: betPosition && betPosition.page,
      },
    });
  }, [activeServiceId, analytics, analyticsProduct, bet, betPosition, serviceIds, servicesExperimentType]);

  const trackDiffPricingShow = useCallback(() => {
    if (diffPricingIds) {
      const { diffPriceIterationId, diffPriceGranuleGuid } = diffPricingIds;

      analytics.send<ITrackDiffPricingShowExtra>({
        action: 'show_diff_pricing',
        extra: {
          type: 'fvu_newest',
          diffPriceGranuleGuid,
          diffPriceIterationId,
        },
      });
    }
  }, [analytics, diffPricingIds]);

  const trackServiceChange = useCallback(() => {
    analytics.send<ITrackChangeServiceExtra>({
      action: 'publish_terms_block_change_vas',
      label: activeServiceId,
      products: [analyticsProduct],
      extra: {
        type: 'fvu_newest_koeff',
        bid: bet,
        position: betPosition && betPosition.offerPosition,
        page_number: betPosition && betPosition.page,
      },
    });
  }, [activeServiceId, analytics, analyticsProduct, bet, betPosition]);

  const trackSliderScroll = useCallback(
    (direction: TSliderScrollDirection) => {
      analytics.send<ITrackSliderScrollExtra>({
        action: 'publish_terms_block_scroll',
        label: direction,
        products: [analyticsProduct],
        extra: {
          type: 'fvu_newest',
        },
      });
    },
    [analytics, analyticsProduct],
  );

  return { trackServicesShow, trackDiffPricingShow, trackServiceChange, trackSliderScroll };
};
